import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import FieldHelp from '@/components/help/FieldHelp';
import helpContent from '@/help/helpContent';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import AgendaResumen from '@/components/agenda/AgendaResumen';
import NuevaCitaDrawer from '@/pages/tenant/NuevaCitaDrawer';
import ClientePanel360 from '@/pages/tenant/ClientePanel360';
import { tenantHasVertical } from '@/lib/verticalAccess';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Check, X, AlertTriangle, Link2, Unlink, ListChecks, PhoneCall, MessageCircle, User } from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameDay, isSameMonth, isToday, parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale';

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200 line-through',
  no_show: 'bg-red-100 text-red-700 border-red-200',
  pending_deposit: 'bg-amber-100 text-amber-700 border-amber-200',
};
const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const VALID_DURATIONS = [15, 30, 45, 60, 90, 120];

function AppointmentChip({ appt, onClick }) {
  const color = STATUS_COLORS[appt.status] || STATUS_COLORS.scheduled;
  return (
    <button onClick={() => onClick(appt)} data-testid={`agenda-appt-chip-${appt.id}`}
      className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded border truncate ${color}`}>
      {format(parseISO(appt.starts_at), 'HH:mm')} {appt.lead_name || appt.title}
    </button>
  );
}

// Sprint 57.6 — las tareas del CRM también se siembran en el calendario para
// que el vendedor/tenedor del lead las vea junto a sus citas, no en una
// pantalla aparte. Solo lectura aquí (la edición sigue viviendo en CRM >
// Tareas) — un clic navega directo a esa pantalla.
function TaskChip({ task, onClick }) {
  return (
    <button onClick={() => onClick(task)} data-testid={`agenda-task-chip-${task.id}`}
      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded border truncate bg-violet-100 text-violet-700 border-violet-200">
      <ListChecks className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" /> {task.title}
    </button>
  );
}

// Sprint 53 (5/N) — reemplaza Hoy/Semana/Calendario-por-recurso/Todas (4
// pantallas fragmentadas, ninguna con mes ni edición) por un solo calendario
// día/semana/mes al estilo Google Calendar, con navegación adelante/atrás y
// edición real de citas ya creadas — hoy no existía ningún modal de edición
// en ninguna de las 4 vistas anteriores, solo cambios de estado.
// Eje 'agenda de servicios' (Front Desk + recursos), espejo del backend
// VERTICALES_CON_AGENDA_DE_SERVICIOS: relacion + talleres + automotriz postventa.
const VERTICALES_DRAWER = ['clinic','spa','health','beauty','fitness','education','professional_services','workshop','automotive'];

export default function AgendaCalendarUnified() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(new Date());
  // Lo reporta el resumen de arriba, que ya consulta /agenda/status.
  const [sinCitas, setSinCitas] = useState(false);
  const [drawerNueva, setDrawerNueva] = useState(false);
  const [dictVertical, setDictVertical] = useState(null);
  const [panelLeadId, setPanelLeadId] = useState(null);
  const [drawerPersona, setDrawerPersona] = useState(null);
  const [drawerFecha, setDrawerFecha] = useState(null);
  const [appts, setAppts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockForm, setBlockForm] = useState(null); // { starts_at, ends_at, title }
  const [savingBlock, setSavingBlock] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [createSlot, setCreateSlot] = useState(null); // { day: Date } popup tarea | cita
  const [createKind, setCreateKind] = useState('task'); // task | appointment
  const [createForm, setCreateForm] = useState({ title: '', due_at: '', time: '10:00', notes: '', function_key: '' });
  const [taskFunctions, setTaskFunctions] = useState([]);
  const [savingCreate, setSavingCreate] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [services, setServices] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  // S57 (4/N) — conectar el Google Calendar personal del usuario logueado
  // (no del tenant) — cada vendedor conecta el suyo.
  const [googleConn, setGoogleConn] = useState(null);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [outlookConn, setOutlookConn] = useState(null);
  const [connectingOutlook, setConnectingOutlook] = useState(false);

  const range = useMemo(() => {
    if (view === 'month') {
      return { from: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }), to: addDays(endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }), 1) };
    }
    if (view === 'week') {
      return { from: startOfWeek(anchor, { weekStartsOn: 1 }), to: addDays(endOfWeek(anchor, { weekStartsOn: 1 }), 1) };
    }
    return { from: new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()), to: addDays(anchor, 1) };
  }, [view, anchor]);

  useEffect(() => { api.get('/tenant/business/dictionary').then(r => setDictVertical(r.data || {})).catch(() => setDictVertical({})); }, []);
  // Puente Expediente -> Agenda (Fase 6): abrir Nueva cita con la persona ya cargada.
  useEffect(() => {
    const lid = searchParams.get('nueva_cita_lead');
    if (!lid) return;
    setDrawerPersona({ lead_id: lid, display_name: searchParams.get('nombre') || '' });
    setDrawerNueva(true);
    const next = new URLSearchParams(searchParams); next.delete('nueva_cita_lead'); next.delete('nombre');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tenant/agenda/appointments', {
        params: { from_dt: range.from.toISOString(), to_dt: range.to.toISOString(), page_size: 300 },
      });
      setAppts((r.data?.items || []).filter(a => a.status !== 'cancelled'));
    } catch { /* best-effort */ }
    try {
      const b = await api.get('/tenant/agenda/blocks', {
        params: { from_dt: range.from.toISOString(), to_dt: range.to.toISOString() },
      });
      setBlocks(b.data?.items || []);
    } catch { setBlocks([]); }
    // Sprint 57.6 — sembrar las tareas del CRM en el mismo calendario.
    try {
      const t = await api.get('/tenant/crm/tasks', {
        params: {
          status: 'pending', page_size: 300,
          due_from: range.from.toISOString().slice(0, 10),
          due_to: range.to.toISOString().slice(0, 10),
        },
      });
      setTasks(t.data?.items || []);
    } catch { setTasks([]); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenant/agenda/services').then(r => setServices(r.data?.items || [])).catch(() => {});
    api.get('/tenant/team').then(r => setTeamUsers(r.data?.items || r.data || [])).catch(() => {});
    api.get('/tenant/crm/task-functions').then(r => setTaskFunctions(r.data?.items || [])).catch(() => {});
  }, []);

  const loadGoogleStatus = useCallback(() => {
    api.get('/tenant/agenda/google/status').then(r => setGoogleConn(r.data)).catch(() => {
      api.get('/tenant/oauth/google/status').then(r => setGoogleConn(r.data)).catch(() => {});
    });
  }, []);
  const loadOutlookStatus = useCallback(() => {
    api.get('/tenant/agenda/outlook/status').then(r => setOutlookConn(r.data)).catch(() => {
      api.get('/tenant/oauth/outlook/status').then(r => setOutlookConn(r.data)).catch(() => {});
    });
  }, []);
  useEffect(() => { loadGoogleStatus(); loadOutlookStatus(); }, [loadGoogleStatus, loadOutlookStatus]);

  // S57 (4/N) — al volver del consentimiento de Google, la URL trae
  // ?oauth=google&connected=1|0 — mostramos el resultado una sola vez y
  // limpiamos los parámetros para que un refresh no repita el toast.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    if (oauth !== 'google' && oauth !== 'outlook') return;
    const connected = searchParams.get('connected') === '1';
    if (oauth === 'outlook') {
      const reason = searchParams.get('reason');
      if (connected) {
        toast.success('Outlook Calendar conectado');
        api.get('/tenant/agenda/outlook/status').then(r => setOutlookConn(r.data)).catch(() => {});
      } else if (reason === 'invalid_client_secret') {
        toast.error('Microsoft rechazó el Client Secret. En Super Admin → Outlook pega el VALOR del secreto (Value), no el Secret ID.');
      } else if (reason === 'redirect_mismatch') {
        toast.error('Redirect URI en Azure no coincide con el de CavernicolApp.');
      } else {
        toast.error(reason ? `No se pudo conectar Outlook (${reason})` : 'No se pudo conectar Outlook. Revisa el Client Secret en Super Admin.');
      }
    } else {
      toast[connected ? 'success' : 'error'](connected ? 'Google Calendar conectado' : 'No se pudo conectar Google Calendar');
      loadGoogleStatus();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('oauth'); next.delete('connected'); next.delete('reason');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const { data } = await api.get('/tenant/oauth/google/authorize');
      window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail?.detail : detail) || 'No se pudo iniciar la conexión con Google');
      setConnectingGoogle(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('¿Desconectar tu Google Calendar? El calendario del sistema dejará de sincronizarse con tu cuenta de Google.')) return;
    try {
      await api.post('/tenant/oauth/google/disconnect');
      toast.success('Google Calendar desconectado');
      loadGoogleStatus();
    } catch (e) { toast.error('No se pudo desconectar'); }
  };

  const resyncGoogle = async () => {
    try {
      const { data } = await api.post('/tenant/agenda/google/resync');
      toast.success(`Google Calendar: ${data?.attempted || 0} citas revisadas`);
      loadGoogleStatus();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo re-sincronizar con Google');
    }
  };

  const connectOutlook = async () => {
    setConnectingOutlook(true);
    try {
      const { data } = await api.get('/tenant/oauth/outlook/authorize');
      window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail?.detail : detail) || 'No se pudo iniciar la conexión con Outlook');
      setConnectingOutlook(false);
    }
  };
  const disconnectOutlook = async () => {
    if (!window.confirm('¿Desconectar tu Outlook Calendar?')) return;
    try {
      await api.post('/tenant/oauth/outlook/disconnect');
      toast.success('Outlook Calendar desconectado');
      loadOutlookStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo desconectar Outlook'); }
  };
  const resyncOutlook = async () => {
    try {
      const { data } = await api.post('/tenant/agenda/outlook/resync');
      toast.success(`Outlook: ${data?.attempted || 0} citas revisadas`);
      loadOutlookStatus();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo re-sincronizar con Outlook');
    }
  };

  const shift = (dir) => {
    if (view === 'month') setAnchor(a => addMonths(a, dir));
    else if (view === 'week') setAnchor(a => addWeeks(a, dir));
    else setAnchor(a => addDays(a, dir));
  };

  const openEdit = (appt) => {
    setEditing(appt);
    const d = parseISO(appt.starts_at);
    setEditForm({
      title: appt.title || '', date: format(d, 'yyyy-MM-dd'), time: format(d, 'HH:mm'),
      duration_minutes: appt.duration_minutes, notes: appt.notes || '',
      service_id: appt.service_id || '', assigned_to_user_id: appt.assigned_to_user_id || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const starts_at = new Date(`${editForm.date}T${editForm.time}:00`).toISOString();
      await api.put(`/tenant/agenda/appointments/${editing.id}`, {
        title: editForm.title, starts_at, duration_minutes: editForm.duration_minutes,
        notes: editForm.notes, service_id: editForm.service_id || null,
        assigned_to_user_id: editForm.assigned_to_user_id || null,
      });
      toast.success('Cita actualizada');
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo actualizar'); }
    finally { setSaving(false); }
  };

  const transitionStatus = async (action) => {
    if (!editing) return;
    try {
      await api.patch(`/tenant/agenda/appointments/${editing.id}/${action}`);
      toast.success('Actualizado');
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const monthDays = useMemo(() => {
    const days = [];
    let d = range.from;
    while (d < range.to) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [range]);

  const apptsForDay = (day) => appts.filter(a => isSameDay(parseISO(a.starts_at), day)).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const blocksForDay = (day) => blocks.filter(b => isSameDay(parseISO(b.starts_at), day));
  const tasksForDay = (day) => tasks.filter(t => {
    const raw = t.due_at || (t.due_date ? `${t.due_date}T12:00:00` : null);
    return raw && isSameDay(parseISO(raw), day);
  });
  const openTask = (task) => {
    // Edición en CRM panel de tareas (modal de agenda solo crea; detalle en CRM)
    navigate(task?.id ? `/tenant/crm/tasks?lead_id=${task.crm_lead_id || ''}` : '/tenant/crm/tasks');
  };

  const openCreateSlot = (day) => {
    const d = day instanceof Date ? day : new Date(day);
    // Verticales de relacion: clic en una fecha abre el MISMO drawer de
    // nueva cita, ya puesto en ese dia (unifica con el boton 'Nueva cita').
    if (tenantHasVertical(dictVertical, VERTICALES_DRAWER)) {
      setDrawerPersona(null); setDrawerFecha(format(d, 'yyyy-MM-dd')); setDrawerNueva(true);
      return;
    }
    setCreateKind('task');
    setCreateForm({
      title: '',
      due_at: `${format(d, 'yyyy-MM-dd')}T10:00`,
      time: '10:00',
      notes: '',
      function_key: '',
    });
    setCreateSlot({ day: d });
  };

  const saveCreateSlot = async () => {
    if (!createSlot) return;
    if (createKind === 'appointment') {
      const dayStr = format(createSlot.day, 'yyyy-MM-dd');
      navigate(`/tenant/agenda/new?date=${dayStr}&time=${createForm.time || '10:00'}`);
      setCreateSlot(null);
      return;
    }
    if (!createForm.title.trim()) { toast.error('Escribe un título para la tarea'); return; }
    if (!createForm.due_at) { toast.error('Fecha y hora requeridas'); return; }
    setSavingCreate(true);
    try {
      await api.post('/tenant/crm/tasks', {
        title: createForm.title.trim(),
        description: createForm.notes || null,
        due_at: new Date(createForm.due_at).toISOString(),
        activity_label: createForm.title.trim(),
        function_key: createForm.function_key || null,
      });
      toast.success('Tarea creada. Si tienes Google Calendar, se sincroniza al responsable.');
      setCreateSlot(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear la tarea');
    } finally {
      setSavingCreate(false);
    }
  };

  const openBlockForm = (day) => {
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(day);
    end.setHours(10, 0, 0, 0);
    setBlockForm({
      starts_at: start.toISOString().slice(0, 16),
      ends_at: end.toISOString().slice(0, 16),
      title: 'No disponible',
      whole_business: true,
    });
  };

  const saveBlock = async () => {
    if (!blockForm?.starts_at || !blockForm?.ends_at) return;
    setSavingBlock(true);
    try {
      await api.post('/tenant/agenda/blocks', {
        starts_at: new Date(blockForm.starts_at).toISOString(),
        ends_at: new Date(blockForm.ends_at).toISOString(),
        title: blockForm.title || 'Bloqueado',
        assigned_to_user_id: blockForm.whole_business ? null : undefined,
      });
      toast.success('Espacio bloqueado — la IA no podrá agendar ahí');
      setBlockForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingBlock(false);
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm('¿Quitar este bloqueo?')) return;
    try {
      await api.delete(`/tenant/agenda/blocks/${id}`);
      toast.success('Bloqueo eliminado');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const headerLabel = view === 'month'
    ? format(anchor, 'MMMM yyyy', { locale: es })
    : view === 'week'
      ? `Semana del ${format(startOfWeek(anchor, { weekStartsOn: 1 }), 'd MMM', { locale: es })}`
      : format(anchor, "EEEE d 'de' MMMM", { locale: es });

  return (
    <div className="space-y-3" data-testid="agenda-calendar-unified">
      <SectionHelp
        module="agenda_calendar"
        title={helpContent.agenda_calendar.title}
        description={helpContent.agenda_calendar.description}
        whatItDoesNot={helpContent.agenda_calendar.whatItDoesNot}
      />

      {/* Aquí vivía la pantalla «Inicio». Solo se trajeron los números y la
          próxima cita: sus cuatro botones llevaban a sitios a los que ya se
          llega desde aquí mismo o desde el menú. */}
      <AgendaResumen onSinCitas={setSinCitas} />

      {sinCitas && (
        <EmptyStateGuide module="agenda_home"
          message="Todavía no tienes citas agendadas. Crea una con «Nueva cita», desde un lead en el CRM, o deja que la IA las agende por llamada o WhatsApp respetando tu horario."
          ctaLabel="Nueva cita"
          onCta={() => (tenantHasVertical(dictVertical, VERTICALES_DRAWER) ? (setDrawerPersona(null), setDrawerFecha(null), setDrawerNueva(true)) : navigate('/tenant/agenda/new'))} />
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="agenda-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setAnchor(new Date())} data-testid="agenda-cal-today">Hoy</Button>
          <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="agenda-cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <h2 className="text-base font-semibold text-[#0F172A] capitalize ml-2">{headerLabel}</h2>
          <HelpTooltip text={helpContent.agenda_calendar.fields.views.help} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 border border-[#E2E8F0] rounded-lg p-0.5">
            {[['day', 'Día'], ['week', 'Semana'], ['month', 'Mes']].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`agenda-cal-view-${k}`}
                className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${view === k ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => openBlockForm(anchor)} data-testid="agenda-cal-block">
              Bloquear espacio
            </Button>
            <HelpTooltip text={helpContent.agenda_calendar.fields.block_space.help} />
          </div>
          <Button size="sm" onClick={() => (tenantHasVertical(dictVertical, VERTICALES_DRAWER) ? (setDrawerPersona(null), setDrawerFecha(null), setDrawerNueva(true)) : navigate('/tenant/agenda/new'))} data-testid="agenda-cal-new">
            <Plus className="w-4 h-4 mr-1" /> Nueva cita
          </Button>
          <NuevaCitaDrawer open={drawerNueva} onClose={() => setDrawerNueva(false)} onCreated={load} personaInicial={drawerPersona} fecha={drawerFecha} />
          <ClientePanel360 open={!!panelLeadId} leadId={panelLeadId} onClose={() => setPanelLeadId(null)}
                           onNuevaCita={(per) => { setPanelLeadId(null); setDrawerPersona(per); setDrawerFecha(null); setDrawerNueva(true); }} />
        </div>
      </div>
      <p className="text-[11px] text-[#94A3B8] flex flex-wrap items-center gap-1">
        <span>Admin/gerente ve todas las citas; el vendedor solo las suyas.</span>
        <HelpTooltip text={helpContent.agenda_calendar.fields.roles.help} />
        <span>· Bloqueos = la IA no agenda ahí.</span>
        <HelpTooltip text={helpContent.agenda_calendar.fields.business_hours.help} />
      </p>

      {/* S57 (4/N) — conecta el Google Calendar personal del usuario
          logueado (no el del negocio). Deja la conexión lista y guardada
          de verdad; la sincronización de eventos en ambos sentidos es la
          siguiente entrega sobre esta misma base. */}
      <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5" data-testid="agenda-google-connect">
        <div className="flex items-center gap-2 text-sm">
          <CalendarDays className="w-4 h-4 text-[#64748B]" />
          {googleConn?.connected ? (
            <span className="text-[#0F172A] flex items-center gap-1">
              Google Calendar conectado{googleConn.email ? ` — ${googleConn.email}` : ''}{googleConn.pending_sync ? ` · ${googleConn.pending_sync} por sincronizar` : ''}{googleConn.synced_appointments ? ` · ${googleConn.synced_appointments} en Google` : ''}
              <HelpTooltip text={helpContent.agenda_calendar.fields.google_connect.help} />
            </span>
          ) : (
            <span className="text-[#64748B] flex items-center gap-1">
              Tu Google Calendar no está conectado
              <HelpTooltip text={helpContent.agenda_calendar.fields.google_connect.help} />
            </span>
          )}
        </div>
        {googleConn?.connected ? (
          <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={resyncGoogle} data-testid="agenda-google-resync">
            Re-sincronizar
          </Button>
          <Button size="sm" variant="outline" onClick={disconnectGoogle} data-testid="agenda-google-disconnect">
            <Unlink className="w-3.5 h-3.5 mr-1.5" /> Desconectar
          </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={connectGoogle} disabled={connectingGoogle} data-testid="agenda-google-connect-btn">
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> {connectingGoogle ? 'Conectando...' : 'Conectar Google Calendar'}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 mt-2" data-testid="agenda-outlook-connect">
        <div className="flex items-center gap-2 text-sm">
          <CalendarDays className="w-4 h-4 text-[#0078D4]" />
          {outlookConn?.needs_reconnect ? (
            <span className="text-amber-700 flex items-center gap-1">
              Outlook necesita reconexión{outlookConn.email ? ` — ${outlookConn.email}` : ''}
              <span className="text-[11px] text-amber-600">(el acceso expiró; vuelve a conectar)</span>
            </span>
          ) : outlookConn?.connected ? (
            <span className="text-[#0F172A] flex items-center gap-1 flex-wrap">
              Outlook conectado{outlookConn.email ? ` — ${outlookConn.email}` : ''}
              {outlookConn.pending_sync ? ` · ${outlookConn.pending_sync} por sincronizar` : ''}
              {outlookConn.synced_appointments ? ` · ${outlookConn.synced_appointments} en Outlook` : ''}
              {outlookConn.has_refresh_token === false ? (
                <span className="text-[11px] text-amber-600"> · sin renovación automática</span>
              ) : null}
            </span>
          ) : (
            <span className="text-[#64748B]">Tu Outlook Calendar no está conectado</span>
          )}
        </div>
        {outlookConn?.connected && !outlookConn?.needs_reconnect ? (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={resyncOutlook} data-testid="agenda-outlook-resync">Re-sincronizar</Button>
            <Button size="sm" variant="outline" onClick={disconnectOutlook} data-testid="agenda-outlook-disconnect">
              <Unlink className="w-3.5 h-3.5 mr-1.5" /> Desconectar
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {outlookConn?.needs_reconnect ? (
              <Button size="sm" variant="outline" onClick={disconnectOutlook} data-testid="agenda-outlook-disconnect">
                <Unlink className="w-3.5 h-3.5 mr-1.5" /> Limpiar
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={connectOutlook} disabled={connectingOutlook} data-testid="agenda-outlook-connect-btn">
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> {connectingOutlook ? 'Conectando...' : (outlookConn?.needs_reconnect ? 'Reconectar Outlook' : 'Conectar Outlook')}
            </Button>
          </div>
        )}
      </div>


      {loading && <p className="text-xs text-[#94A3B8]">Cargando...</p>}

      {view === 'month' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="agenda-cal-month">
          <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            {WEEKDAY_LABELS.map(l => <div key={l} className="text-[11px] font-medium text-[#64748B] text-center py-2">{l}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map(day => {
              const dayAppts = apptsForDay(day);
              const dayBlocks = blocksForDay(day);
              const dayTasks = tasksForDay(day);
              const inMonth = isSameMonth(day, anchor);
              return (
                <div key={day.toISOString()}
                  className={`min-h-[92px] border-b border-r border-[#F1F5F9] p-1 ${inMonth ? '' : 'bg-[#FAFBFC]'} cursor-pointer`}
                  data-testid={`agenda-cal-day-${format(day, 'yyyy-MM-dd')}`}
                  onDoubleClick={() => openCreateSlot(day)}
                  onClick={(e) => {
                    // Clic en el fondo del día (no en chips) → crear
                    if (e.target === e.currentTarget || e.target.dataset?.slotEmpty === '1') openCreateSlot(day);
                  }}
                >
                  <button onClick={(e) => { e.stopPropagation(); setAnchor(day); setView('day'); }}
                    className={`text-[11px] font-medium mb-1 px-1 rounded ${isToday(day) ? 'bg-[#F72585] text-white' : inMonth ? 'text-[#0F172A]' : 'text-[#CBD5E1]'}`}>
                    {format(day, 'd')}
                  </button>
                  <div className="space-y-0.5" data-slot-empty={dayAppts.length + dayTasks.length + dayBlocks.length === 0 ? '1' : undefined}>
                    {dayBlocks.slice(0, 2).map(b => (
                      <button key={b.id} type="button" onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }}
                        className="w-full text-left text-[10px] px-1.5 py-0.5 rounded border truncate bg-slate-200 text-slate-700 border-slate-300"
                        title="Clic para quitar bloqueo">
                        🚫 {format(parseISO(b.starts_at), 'HH:mm')} {b.title || 'Bloqueado'}
                      </button>
                    ))}
                    {dayAppts.slice(0, 3).map(a => (
                      <div key={a.id} onClick={(e) => e.stopPropagation()}>
                        <AppointmentChip appt={a} onClick={openEdit} />
                      </div>
                    ))}
                    {dayTasks.slice(0, 2).map(t => (
                      <div key={t.id} onClick={(e) => e.stopPropagation()}>
                        <TaskChip task={t} onClick={openTask} />
                      </div>
                    ))}
                    {(dayAppts.length > 3 || dayTasks.length > 2) && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setAnchor(day); setView('day'); }} className="text-[10px] text-[#94A3B8] hover:text-[#0F172A]">
                        +{Math.max(0, dayAppts.length - 3) + Math.max(0, dayTasks.length - 2)} más
                      </button>
                    )}
                    {dayAppts.length + dayTasks.length === 0 && (
                      <button type="button" data-slot-empty="1" onClick={() => openCreateSlot(day)}
                        className="w-full text-[10px] text-[#CBD5E1] hover:text-[#F72585] py-1">
                        + crear
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'week' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="agenda-cal-week">
          <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            {monthDays.slice(0, 7).map(day => (
              <div key={day.toISOString()} className="text-center py-2">
                <div className="text-[11px] text-[#64748B]">{WEEKDAY_LABELS[(day.getDay() + 6) % 7]}</div>
                <button onClick={() => { setAnchor(day); setView('day'); }}
                  className={`text-sm font-semibold px-2 rounded ${isToday(day) ? 'bg-[#F72585] text-white' : 'text-[#0F172A]'}`}>
                  {format(day, 'd')}
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x divide-[#F1F5F9]">
            {monthDays.slice(0, 7).map(day => {
              const dayAppts = apptsForDay(day);
              const dayTasks = tasksForDay(day);
              return (
                <div key={day.toISOString()} className="min-h-[300px] p-1 space-y-1 cursor-pointer"
                  onClick={() => openCreateSlot(day)} data-testid={`agenda-cal-week-slot-${format(day, 'yyyy-MM-dd')}`}>
                  {dayAppts.length === 0 && dayTasks.length === 0 ? (
                    <p className="text-[10px] text-[#CBD5E1] text-center pt-4">+ crear</p>
                  ) : (
                    <>
                      {dayAppts.map(a => (
                        <div key={a.id} onClick={(e) => e.stopPropagation()}><AppointmentChip appt={a} onClick={openEdit} /></div>
                      ))}
                      {dayTasks.map(t => (
                        <div key={t.id} onClick={(e) => e.stopPropagation()}><TaskChip task={t} onClick={openTask} /></div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'day' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid="agenda-cal-day">
          {apptsForDay(anchor).length === 0 && tasksForDay(anchor).length === 0 ? (
            <div className="text-center py-10">
              <CalendarDays className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
              <p className="text-sm text-[#94A3B8]">Sin citas ni tareas este día</p>
              <Button size="sm" className="mt-3" onClick={() => openCreateSlot(anchor)} data-testid="agenda-day-create">
                <Plus className="w-3.5 h-3.5 mr-1" /> Crear tarea o cita
              </Button>
            </div>
          ) : (
            <div className="divide-y" data-testid="agenda-cal-day-list">
              {apptsForDay(anchor).map(a => (
                <button key={a.id} onClick={() => openEdit(a)} data-testid={`agenda-cal-day-item-${a.id}`}
                  className="w-full text-left py-3 flex items-center justify-between hover:bg-[#F8FAFC] px-2 -mx-2 rounded">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">
                      {a.display_code && <span className="font-mono font-normal text-[#94A3B8] mr-1.5">{a.display_code}</span>}
                      {a.lead_name || a.title}
                    </p>
                    <p className="text-xs text-[#64748B]">{format(parseISO(a.starts_at), 'HH:mm')} · {a.duration_minutes} min</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.google_synced && <CalendarDays className="w-3.5 h-3.5 text-[#4285F4]" title="Sincronizada con Google Calendar" />}
                    {a.outlook_synced && <CalendarDays className="w-3.5 h-3.5 text-[#0078D4]" title="Sincronizada con Outlook Calendar" />}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[a.status] || STATUS_COLORS.scheduled}`}>{a.status}</span>
                  </div>
                </button>
              ))}
              {tasksForDay(anchor).map(t => (
                <button key={t.id} onClick={openTask} data-testid={`agenda-cal-day-task-${t.id}`}
                  className="w-full text-left py-3 flex items-center justify-between hover:bg-[#F8FAFC] px-2 -mx-2 rounded">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
                      <ListChecks className="w-3.5 h-3.5 text-violet-600" /> {t.title}
                    </p>
                    <p className="text-xs text-[#64748B]">Tarea CRM</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border bg-violet-100 text-violet-700 border-violet-200">pendiente</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="agenda-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Editar cita
              <HelpTooltip text="Cambia fecha, hora, duración, servicio o vendedor. Solo citas en estado agendada se pueden editar. El vendedor solo edita las suyas; admin/gerente todas." />
            </DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <Button size="sm" variant="outline" className="w-full justify-start"
                      onClick={() => { const lid = editing?.crm_lead_id; setEditing(null); if (lid) setPanelLeadId(lid); }}
                      data-testid="agenda-edit-ver-360">
                <User className="w-3.5 h-3.5 mr-1" /> Ver ficha 360 del cliente
              </Button>
              {editing?.status !== 'scheduled' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> Solo las citas en estado "agendada" pueden editar fecha/hora — esta ya está {editing?.status}.
                </p>
              )}
              <div>
                <label className="text-xs text-[#64748B]">Título</label>
                <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} data-testid="agenda-edit-title" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[#64748B]">Fecha</label>
                  <Input type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} data-testid="agenda-edit-date" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B]">Hora</label>
                  <Input type="time" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} data-testid="agenda-edit-time" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Duración</label>
                <select value={editForm.duration_minutes} onChange={e => setEditForm(f => ({ ...f, duration_minutes: +e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-edit-duration">
                  {VALID_DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
              </div>
              {services.length > 0 && (
                <div>
                  <label className="text-xs text-[#64748B]">Servicio</label>
                  <select value={editForm.service_id} onChange={e => setEditForm(f => ({ ...f, service_id: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-edit-service">
                    <option value="">Sin servicio</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              {teamUsers.length > 0 && (
                <div>
                  <label className="text-xs text-[#64748B]">Vendedor asignado</label>
                  <select value={editForm.assigned_to_user_id} onChange={e => setEditForm(f => ({ ...f, assigned_to_user_id: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-edit-assignee">
                    <option value="">Sin asignar</option>
                    {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-[#64748B]">Notas</label>
                <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} data-testid="agenda-edit-notes" />
              </div>
              <div className="pt-2 border-t border-[#E2E8F0] space-y-2">
                  {editing?.status === 'scheduled' && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => transitionStatus('complete')} data-testid="agenda-edit-complete">
                        <Check className="w-3.5 h-3.5 mr-1" /> Completar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => transitionStatus('no-show')} data-testid="agenda-edit-noshow">No se presentó</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => transitionStatus('cancel')} data-testid="agenda-edit-cancel">
                        <X className="w-3.5 h-3.5 mr-1" /> Cancelar cita
                      </Button>
                    </div>
                  )}
                  {(editing?.status === 'no_show' || (editing?.status === 'scheduled' && editing?.ends_at && new Date(editing.ends_at) < new Date())) && (
                    <div className="flex flex-wrap gap-2 w-full pt-2 border-t border-dashed border-[#E2E8F0]">
                      <p className="w-full text-[11px] text-[#64748B]">El cliente no llegó o la hora ya pasó — seguimiento:</p>
                      <Button size="sm" variant="outline" disabled={followBusy} data-testid="agenda-noshow-wa"
                        onClick={async () => {
                          setFollowBusy(true);
                          try {
                            await api.post(`/tenant/agenda/appointments/${editing.id}/no-show-followup`, { channel: 'whatsapp' });
                            toast.success('WhatsApp de reagendado enviado (si el canal está conectado)');
                          } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail) || 'No se pudo enviar WhatsApp');
                          }
                          setFollowBusy(false);
                        }}>
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp reagendar
                      </Button>
                      <Button size="sm" variant="outline" disabled={followBusy} data-testid="agenda-noshow-voice"
                        onClick={async () => {
                          setFollowBusy(true);
                          try {
                            await api.post(`/tenant/agenda/appointments/${editing.id}/no-show-followup`, { channel: 'voice' });
                            toast.success('Llamada con IA iniciada');
                          } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail) || 'No se pudo llamar (revisa plan de voz saliente)');
                          }
                          setFollowBusy(false);
                        }}>
                        <PhoneCall className="w-3.5 h-3.5 mr-1" /> Llamar con IA
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-end pt-1 border-t border-[#E2E8F0]">
                    <Button size="sm" onClick={saveEdit} disabled={saving || editing?.status !== 'scheduled'} data-testid="agenda-edit-save">
                      {saving ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  </div>
                </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!createSlot} onOpenChange={(open) => !open && setCreateSlot(null)}>
        <DialogContent data-testid="agenda-create-slot-modal">
          <DialogHeader>
            <DialogTitle>
              Crear en {createSlot?.day ? format(createSlot.day, "d 'de' MMMM", { locale: es }) : '…'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button size="sm" variant={createKind === 'task' ? 'default' : 'outline'} onClick={() => setCreateKind('task')} data-testid="agenda-create-kind-task">
                <ListChecks className="w-3.5 h-3.5 mr-1" /> Tarea CRM
              </Button>
              <Button size="sm" variant={createKind === 'appointment' ? 'default' : 'outline'} onClick={() => setCreateKind('appointment')} data-testid="agenda-create-kind-appt">
                <CalendarDays className="w-3.5 h-3.5 mr-1" /> Cita / servicio
              </Button>
            </div>
            {createKind === 'task' ? (
              <>
                <div>
                  <label className="text-xs text-[#64748B]">Título de la tarea</label>
                  <Input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} data-testid="agenda-create-task-title" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B]">Fecha y hora</label>
                  <Input type="datetime-local" value={createForm.due_at} onChange={e => setCreateForm(f => ({ ...f, due_at: e.target.value }))} />
                </div>
                {taskFunctions.length > 0 && (
                  <div>
                    <label className="text-xs text-[#64748B]">Función IA (opcional)</label>
                    <select
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      value={createForm.function_key || ''}
                      onChange={e => setCreateForm(f => ({ ...f, function_key: e.target.value }))}
                    >
                      <option value="">Sin función</option>
                      {taskFunctions.map(fn => (
                        <option key={fn.key} value={fn.key} disabled={fn.locked}>
                          {fn.locked ? '🔒 ' : ''}{fn.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs text-[#64748B]">Notas</label>
                  <Input value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <p className="text-[11px] text-[#94A3B8]">
                  La tarea entra al calendario del responsable. Si vinculas un lead en CRM, hereda su vendedor y se sincroniza a Google Calendar si está conectado.
                </p>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs text-[#64748B]">Hora de la cita</label>
                  <Input type="time" value={createForm.time} onChange={e => setCreateForm(f => ({ ...f, time: e.target.value }))} />
                </div>
                <p className="text-xs text-[#64748B]">
                  Te llevamos al flujo de nueva cita con el día y la hora listos (servicios del catálogo + recursos).
                </p>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setCreateSlot(null)}>Cancelar</Button>
              <Button size="sm" onClick={saveCreateSlot} disabled={savingCreate} data-testid="agenda-create-slot-save">
                {savingCreate ? 'Guardando…' : createKind === 'task' ? 'Crear tarea' : 'Continuar a cita'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!blockForm} onOpenChange={(open) => !open && setBlockForm(null)}>
        <DialogContent data-testid="agenda-block-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Bloquear espacio
              <HelpTooltip text={helpContent.agenda_block.description} />
            </DialogTitle>
          </DialogHeader>
          {blockForm && (
            <div className="space-y-3">
              <p className="text-xs text-[#64748B]">
                {helpContent.agenda_block.description}
              </p>
              <p className="text-[11px] text-[#94A3B8] italic">
                Lo que NO hace: {helpContent.agenda_block.whatItDoesNot}
              </p>
              <FieldHelp label="Título" help={helpContent.agenda_block.fields.title.help} htmlFor="block-title">
                <Input id="block-title" value={blockForm.title} onChange={e => setBlockForm(f => ({ ...f, title: e.target.value }))} />
              </FieldHelp>
              <div className="grid grid-cols-2 gap-2">
                <FieldHelp label="Desde" help={helpContent.agenda_block.fields.starts_at.help} htmlFor="block-from">
                  <Input id="block-from" type="datetime-local" value={blockForm.starts_at}
                    onChange={e => setBlockForm(f => ({ ...f, starts_at: e.target.value }))} />
                </FieldHelp>
                <FieldHelp label="Hasta" help={helpContent.agenda_block.fields.ends_at.help} htmlFor="block-to">
                  <Input id="block-to" type="datetime-local" value={blockForm.ends_at}
                    onChange={e => setBlockForm(f => ({ ...f, ends_at: e.target.value }))} />
                </FieldHelp>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#0F172A] cursor-pointer">
                <input type="checkbox" checked={!!blockForm.whole_business}
                  onChange={e => setBlockForm(f => ({ ...f, whole_business: e.target.checked }))} />
                Bloquear para todo el negocio (no solo mi agenda)
                <HelpTooltip text={helpContent.agenda_block.fields.whole_business.help} />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setBlockForm(null)}>Cancelar</Button>
                <Button size="sm" onClick={saveBlock} disabled={savingBlock} data-testid="agenda-block-save">
                  {savingBlock ? 'Guardando...' : 'Bloquear'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
